// @flow
import { bindActionCreators } from 'redux';
import { SubmissionError } from 'redux-form';

import type { Dispatch } from 'state/types';
import {
  addNotification,
  ERROR_LEVEL,
} from 'portfolio-shared/state/commands/globalNotificationsCommands';
import {
  FC_FILE_NOT_FOUND,
  FC_INSUFFICIENT_PRIVILEGES,
  FC_INVALID_CREDENTIALS,
  FC_MALFORMED_QUERY_STRING,
  FC_SERVER_UNREACHABLE,
} from 'portfolio-shared/util/faultCode';
import logger from 'portfolio-shared/util/logging';
import localizedStrings from 'util/localizedStrings';
import { LocalizedError } from 'portfolio-shared/util/localizedError';
import { logout as logoutCommand } from 'state/commands/authCommands';
import PortfolioError from 'portfolio-shared/util/PortfolioError';

export function bindActionCreatorsSafe(
  actionCreators: Object,
  optErrorMessages: Object = {},
  isForm?: boolean = false
) {
  return (dispatch: Dispatch) => {
    return {
      // Always provide safe `dispatch` action
      unsafeDispatch: dispatch,
      dispatch: bindSafeDispatch(dispatch, optErrorMessages, isForm),
      ...bindActionCreators(actionCreators, (...args) => {
        return bindSafeDispatch(dispatch, optErrorMessages, isForm)(...args);
      }),
    };
  };
}

/*
 * Only use bindActionCreatorsForm() if you're using an action creator from within the
 * submit method of a redux-form.
 *
 * The redux-form submit method won't be invoked again while there are unresolved
 * promises, so bindSafeDispatch's practice of returning a forever-unresolved
 * promise to prevent the .then handler from being invoked won't work for this
 * case.
 */
export function bindActionCreatorsForm(
  actionCreators: Object,
  optErrorMessages?: Object
) {
  return bindActionCreatorsSafe(actionCreators, optErrorMessages, true);
}

export function bindSafeDispatch(
  dispatch: Dispatch,
  optErrorMessages: Object,
  isForm: boolean
) {
  return (...args: any) => {
    const r = dispatch(...args);
    // $FlowFixMe
    if (r && typeof r.catch === 'function') {
      return r.catch(error => {
        handleError(error, dispatch, optErrorMessages);
        if (isForm) {
          throw new SubmissionError({ _error: error.message });
        } else {
          // Return an empty promise that's never resolved. This way, .then() on
          // callers will not get triggered.
          // This might be a minor memory leak, but probably not.
          return new Promise(() => {});
        }
      });
    }
    return r;
  };
}

export function handleError(
  error: PortfolioError,
  dispatch: Dispatch,
  optErrorMessages?: Object
) {
  let localizedMessage;
  const rawMessage = error.message || error;
  const faultCode = error.faultCode;

  if (optErrorMessages && optErrorMessages.hasOwnProperty(faultCode)) {
    localizedMessage = optErrorMessages[faultCode];
  } else if (faultCode === FC_INSUFFICIENT_PRIVILEGES) {
    localizedMessage = localizedStrings.insufficientPrivileges;
  } else if (faultCode === FC_FILE_NOT_FOUND) {
    localizedMessage = localizedStrings.fileNotFound;
  } else if (faultCode === FC_SERVER_UNREACHABLE) {
    localizedMessage = localizedStrings.serverUnavailable;
    dispatch(logoutCommand());
  } else if (faultCode === FC_INVALID_CREDENTIALS) {
    localizedMessage = localizedStrings.sessionExpired;
    dispatch(logoutCommand());
  } else if (faultCode === FC_MALFORMED_QUERY_STRING) {
    localizedMessage = localizedStrings.invalidSearchFailed;
  } else if (faultCode) {
    // Log the error and present a bland 'unexpected error occurred' message to the user.
    logger.error(`faultCode: ${faultCode} `, rawMessage);
    localizedMessage = localizedStrings.unexpectedError;
  }

  const notification = addNotification(
    localizedMessage ? new LocalizedError(localizedMessage) : rawMessage,
    ERROR_LEVEL
  );
  dispatch(notification);
}



// WEBPACK FOOTER //
// ./src/util/safeDispatch.js